/**
 * Section bodies of Ayarlar, shared by the phone list, the phone sub-screens and the
 * desktop tabs: budgets, approval times, the policy block, preferences, agents, schedule,
 * data and retention, security, about, the change log and the setup order.
 */

import { useEffect, useId, useState } from 'react';
import { formatPolicyValue, realSpendTotal } from '../api/policy-rules.js';
import type { NumberPolicy, NumberPolicyId, PreferencesPatch, Settings } from '../api/settings.js';
import { usePanel, useStore } from '../app/context.js';
import { useMcpAddresses } from '../app/mcp-addresses.js';
import { Link } from '../app/router.js';
import { accusative } from '../app/selectors.js';
import {
  agentsSummary,
  backupMissing,
  connectionPurpose,
  isLive,
  killSwitchUntested,
  policyById,
  safeHttpsUrl,
  setupSummary,
} from '../app/settings.js';
import { AgentGlyph, Icon } from '../components/icons.js';
import { Meter, TriangleGlyph } from '../components/primitives.js';
import {
  AuditList,
  ConnectionPill,
  FlagRow,
  PolicyStepper,
  PrefSwitch,
  Rich,
  RowAction,
  ServiceGlyph,
  StageControl,
  SwitchVisual,
  ThemeRadio,
  usePolicyAction,
} from '../components/settings.js';
import { formatClock, formatDayMonth, formatUsd, formatUsdNumber } from '../lib/format.js';
import { Loading } from './common.js';

/** While the settings load: a loading line, or why they did not load with a retry. */
export function SettingsError() {
  const { settingsError } = usePanel();
  const store = useStore();
  if (!settingsError) return <Loading />;
  return (
    <div className="loading" role="status">
      Ayarlar yüklenemedi: {settingsError}{' '}
      <button type="button" className="text-btn" onClick={() => void store.loadSettings()}>
        Tekrar dene
      </button>
    </div>
  );
}

/** Which way loosens, said once per block instead of on every row. */
export function LoosenLegend() {
  return (
    <p className="pol-legend">
      <Icon name="timer" size={12} className="ink-2" />
      işaretli yön: 1,5 sn basılı tut + gerekçe · diğeri tek dokunuş
    </p>
  );
}

/** A number policy row: label, hint, stepper; a pending suggestion gets its own line below. */
export function PolicyRow({ policy, hint }: { policy: NumberPolicy; hint?: string | undefined }) {
  const labelId = useId();
  const act = usePolicyAction();
  const loosenWord = policy.loosens === 'up' ? 'artırmak' : 'azaltmak';
  const suggestion = policy.suggestion;
  return (
    <div className={suggestion ? 'pol-item pol-item--sug' : 'pol-item'}>
      <div className="pol-row pol-row--num">
        <span className="pol-row__text">
          <span id={labelId} className="pol-row__title">
            {policy.label}
          </span>
          <span className="pol-row__sub">{hint ?? policy.note ?? `${loosenWord}: basılı tut + gerekçe`}</span>
        </span>
        <PolicyStepper policy={policy} labelId={labelId} />
      </div>
      {suggestion ? (
        <div className="pol-suggest">
          <span className="sug-badge">öneri {formatPolicyValue(policy, suggestion.value)}</span>
          <span className="pol-suggest__why">{suggestion.reason}</span>
          <button
            type="button"
            className="row-btn pol-suggest__apply"
            aria-label={`${policy.label} önerisini uygula: ${formatPolicyValue(policy, suggestion.value)}. Gevşetir; 1,5 saniye basılı tut ve gerekçe yaz`}
            onClick={() => act({ kind: 'number', id: policy.id, value: suggestion.value })}
          >
            Uygula
            <Icon name="timer" size={12} className="ink-2" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

function rows(settings: Settings, ids: NumberPolicyId[]): NumberPolicy[] {
  return ids.map((id) => policyById(settings, id)).filter((policy): policy is NumberPolicy => policy !== undefined);
}

export function BudgetsBody({ settings }: { settings: Settings }) {
  const cap = policyById(settings, 'budget_team_cap');
  const total = realSpendTotal(settings);
  return (
    <div className="pol-block">
      <LoosenLegend />
      <h3 className="pol-sub key">Genel Müdür · GM aboneliği</h3>
      <p className="pol-note">API karşılığı olarak izlenir, faturalanmaz; gerçek harcamaya eklenmez.</p>
      {rows(settings, ['budget_gm_month', 'budget_gm_day', 'budget_gm_run']).map((policy) => (
        <PolicyRow key={policy.id} policy={policy} />
      ))}
      <h3 className="pol-sub key">Gerçek harcama · aylık</h3>
      {rows(settings, [
        'budget_temsilci',
        'budget_icerik',
        'budget_denetci',
        'budget_media',
        'budget_infra',
        'budget_sales',
      ]).map((policy) => (
        <PolicyRow key={policy.id} policy={policy} />
      ))}
      {cap ? (
        <>
          <h3 className="pol-sub key">Ekip tavanı</h3>
          <PolicyRow policy={cap} />
          <div className="cap-sum">
            <span>
              Bütçelerin toplamı <span className="mono">{formatUsdNumber(total, 'auto')}</span> /{' '}
              <span className="mono">{formatUsd(cap.value, 'auto')}</span> tavan
            </span>
            <Meter
              value={cap.value > 0 ? total / cap.value : 1}
              label={`Tavanın yüzde ${Math.round((total / cap.value) * 100)} kısmı ayrıldı`}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

export function ApprovalTimesBody({ settings }: { settings: Settings }) {
  return (
    <div className="pol-block">
      <LoosenLegend />
      {rows(settings, ['approval_undo', 'approval_ttl', 'approval_t4_hold']).map((policy) => (
        <PolicyRow key={policy.id} policy={policy} />
      ))}
      <p className="pol-note">T4 kararı her zaman senin: bu bir sert sınır, süreler değişse de değişmez.</p>
    </div>
  );
}

export function MailStageRow({ settings }: { settings: Settings }) {
  const locked = settings.stage.stages.filter((option) => !option.unlocked && option.value > settings.stage.value);
  return (
    <div className="pol-row pol-row--stage">
      <span className="pol-row__text">
        <span className="pol-row__title">{settings.stage.label}</span>
        {locked.map((option) => (
          <span key={option.value} className="pol-row__sub">
            {option.value}: {option.condition}
          </span>
        ))}
      </span>
      <StageControl stage={settings.stage} />
    </div>
  );
}

export function FlagsBlock({ settings, desktop = false }: { settings: Settings; desktop?: boolean }) {
  return (
    <>
      <h3 className="pol-sub key">Özellik anahtarları</h3>
      {settings.flags.map((flag) => (
        <FlagRow key={flag.id} flag={flag} desktop={desktop} />
      ))}
    </>
  );
}

/** The whole policy block (desktop Politika tab, phone /ayarlar/politika). */
export function PolicyBody({ settings, desktop = false }: { settings: Settings; desktop?: boolean }) {
  const discount = policyById(settings, 'discount_cap');
  const sendCap = policyById(settings, 'send_daily_cap');
  return (
    <div className="pol-body">
      <section aria-labelledby="pb-butce" className="pol-group">
        <h2 id="pb-butce" className="pol-group__title">
          Bütçeler
        </h2>
        <BudgetsBody settings={settings} />
      </section>
      <section aria-labelledby="pb-onay" className="pol-group">
        <h2 id="pb-onay" className="pol-group__title">
          Onay süreleri
        </h2>
        <ApprovalTimesBody settings={settings} />
      </section>
      <section aria-labelledby="pb-dis" className="pol-group">
        <h2 id="pb-dis" className="pol-group__title">
          Para ve dış temas
        </h2>
        {discount ? <PolicyRow policy={discount} /> : null}
        <MailStageRow settings={settings} />
        {sendCap ? (
          <PolicyRow
            policy={sendCap}
            hint={sendCap.ceiling ? `${sendCap.note ?? ''} · ${sendCap.ceiling.reason}` : undefined}
          />
        ) : null}
        <FlagsBlock settings={settings} desktop={desktop} />
      </section>
    </div>
  );
}

export function NotificationsBody({ settings }: { settings: Settings }) {
  const store = useStore();
  const n = settings.preferences.notifications;
  const telegram = settings.connections.find((item) => item.id === 'telegram');
  const set = (patch: NonNullable<PreferencesPatch['notifications']>) =>
    void store.updatePreferences({ notifications: patch });
  return (
    <div className="pol-block">
      {telegram && !isLive(telegram) ? (
        <Link to="/ayarlar/baglanti/telegram" className="st-notice">
          <Icon name="send" size={16} />
          <span>Telegram botu bağlı değil; bildirimler bot bağlanınca başlar.</span>
          <Icon name="chevron-right" size={16} className="ink-3" />
        </Link>
      ) : null}
      <PrefSwitch
        label="Onaylar"
        hint="Telegram anlık · link panele açılır"
        checked={n.approvals}
        onChange={(value) => set({ approvals: value })}
      />
      <PrefSwitch
        label="Uyarılar"
        hint="Telegram anlık · hata, bütçe, durma"
        checked={n.alerts}
        onChange={(value) => set({ alerts: value })}
      />
      <PrefSwitch
        label="Günlük rapor"
        hint={<span className="mono">{n.dailyReportAt}</span>}
        checked={n.dailyReport}
        onChange={(value) => set({ dailyReport: value })}
      />
      <PrefSwitch
        label="Haftalık rapor"
        hint={n.weeklyReportAt}
        checked={n.weeklyReport}
        onChange={(value) => set({ weeklyReport: value })}
      />
      <PrefSwitch
        label="Sessiz saatler"
        hint={
          <>
            <span className="mono">
              {n.quietFrom}-{n.quietTo}
            </span>{' '}
            · Telegram sessiz
          </>
        }
        checked={n.quietHours}
        onChange={(value) => set({ quietHours: value })}
      />
    </div>
  );
}

export function LanguageBody({ settings }: { settings: Settings }) {
  const store = useStore();
  const name = useId();
  const currency = settings.preferences.currency;
  return (
    <div className="pol-block">
      <div className="pol-row">
        <span className="pol-row__text">
          <span className="pol-row__title">Dil</span>
          <span className="pol-row__sub">Türkçe · şimdilik tek dil; müşteriye giden metinler İngilizce</span>
        </span>
      </div>
      <div className="pol-row">
        <span className="pol-row__text">
          <span className="pol-row__title">Saat dilimi</span>
          <span className="pol-row__sub">
            <span className="mono">{settings.preferences.timezone}</span> · takvim ve bütün saatler
          </span>
        </span>
      </div>
      <div className="pol-row pol-row--pref">
        <span className="pol-row__text">
          <span className="pol-row__title" aria-hidden="true">
            Para gösterimi
          </span>
          <span className="pol-row__sub">tutarlar hep dolar; sadece yazımı değişir</span>
        </span>
        <fieldset className="seg-radio seg-radio--currency">
          <legend className="sr-only">Para gösterimi</legend>
          {(
            [
              ['symbol', '1.234,56 $'],
              ['code', '1.234,56 USD'],
            ] as const
          ).map(([id, label]) => (
            <label key={id} className={`seg-radio__opt${currency === id ? ' seg-radio__opt--on' : ''}`}>
              <input
                type="radio"
                className="seg-radio__input"
                name={name}
                value={id}
                checked={currency === id}
                onChange={() => void store.updatePreferences({ currency: id })}
              />
              <span className="mono">{label}</span>
            </label>
          ))}
        </fieldset>
      </div>
    </div>
  );
}

export function ThemeRow() {
  return (
    <div className="pol-row pol-row--theme">
      <span className="pol-row__title" aria-hidden="true">
        Tema
      </span>
      <ThemeRadio />
    </div>
  );
}

export function PreferencesBody({ settings }: { settings: Settings }) {
  return (
    <div className="pol-body">
      <section aria-labelledby="pr-gorunum" className="pol-group">
        <h2 id="pr-gorunum" className="pol-group__title">
          Görünüm
        </h2>
        <ThemeRow />
      </section>
      <section aria-labelledby="pr-bildirim" className="pol-group">
        <h2 id="pr-bildirim" className="pol-group__title">
          Bildirimler
        </h2>
        <NotificationsBody settings={settings} />
      </section>
      <section aria-labelledby="pr-dil" className="pol-group">
        <h2 id="pr-dil" className="pol-group__title">
          Dil ve bölge
        </h2>
        <LanguageBody settings={settings} />
      </section>
    </div>
  );
}

/** Agents: enabled switch (off is one tap, on is hold plus reason), model, budget, last run, pause. */
export function AgentsBody({ settings }: { settings: Settings }) {
  const state = usePanel();
  const store = useStore();
  const act = usePolicyAction();
  return (
    <div className="pol-block">
      <p className="pol-note">
        {agentsSummary(settings)}. Ajanlar token tutmaz; araçları bmu-tools sunucusunda zorlanır.
      </p>
      <ul className="agent-cfg" aria-label="Ajanlar">
        {settings.agents.map((agent) => {
          const budget = policyById(settings, agent.budgetPolicy);
          const live = state.snapshot?.agents.find((item) => item.id === agent.id);
          const paused = live?.state === 'paused';
          return (
            <li key={agent.id} className="agent-cfg__item">
              <AgentGlyph shape={agent.shape} size={16} className="agent-cfg__glyph" />
              <span className="agent-cfg__text">
                <span className="agent-cfg__name">{agent.name}</span>
                <span className="agent-cfg__meta">
                  {agent.provider} · <span className="mono">{agent.model}</span> ·{' '}
                  <span className="agent-cfg__alias">{agent.alias}</span>
                </span>
                <span className="agent-cfg__meta">
                  bütçe <span className="mono">{budget ? formatPolicyValue(budget, budget.value) : '-'}</span>
                  {budget?.unit === 'usd' && agent.provider === 'GM aboneliği' ? ' API karşılığı' : ''} · son çalışma{' '}
                  {agent.lastRunAt ? (
                    <span className="mono">
                      {formatDayMonth(agent.lastRunAt)} {formatClock(agent.lastRunAt)}
                    </span>
                  ) : (
                    'yok'
                  )}
                </span>
                {agent.blockedBy ? (
                  <span className="agent-cfg__blocked">
                    Bekliyor:{' '}
                    {agent.blockedByConnection ? (
                      <Link to={`/ayarlar/baglanti/${agent.blockedByConnection}`}>{agent.blockedBy}</Link>
                    ) : (
                      agent.blockedBy
                    )}
                  </span>
                ) : null}
              </span>
              <span className="agent-cfg__controls">
                <button
                  type="button"
                  className="btn btn--secondary btn--small"
                  aria-label={
                    paused ? `${agent.name}: devam ettir` : `${accusative(agent.name)} duraklat: mevcut adımı bitirir`
                  }
                  onClick={() => void (paused ? store.resumeAgent(agent.id) : store.pauseAgent(agent.id))}
                >
                  <Icon name={paused ? 'play' : 'pause'} size={14} />
                  {paused ? 'Devam et' : 'Duraklat'}
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={agent.enabled}
                  aria-label={`${agent.name}: ${agent.enabled ? 'açık. Kapatmak sıkılaştırır, tek dokunuş' : 'kapalı. Açmak gevşetir; 1,5 saniye basılı tut ve gerekçe yaz'}`}
                  className="switch-hit"
                  onClick={() => act({ kind: 'agent', id: agent.id, enabled: !agent.enabled })}
                >
                  <SwitchVisual on={agent.enabled} />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const OWNER_NAMES: Record<string, string> = {
  executor: 'Dağıtıcı',
  worker: 'Zamanlayıcı',
  gm: 'Genel Müdür',
  temsilci: 'Temsilci',
  icerik: 'İçerik ve SEO',
  denetci: 'Denetçi',
};

export function ScheduleBody({ settings }: { settings: Settings }) {
  const schedule = settings.schedule;
  return (
    <div className="pol-block">
      <ol className="sched" aria-label="Günlük ritim">
        {schedule.jobs.map((job) => (
          <li key={job.id} className={job.enabled ? 'sched__item' : 'sched__item sched__item--off'}>
            <span className="sched__time mono">{job.time}</span>
            <span className="sched__text">
              <span className="sched__title">{job.title}</span>
              <span className="sched__meta">
                {job.days} · {OWNER_NAMES[job.owner] ?? job.owner}
                {job.enabled ? null : ' · kapalı'}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="pol-note">
        Saat dilimi <span className="mono">{schedule.timezone}</span>. {schedule.note}
      </p>
      <a
        href={safeHttpsUrl(`${settings.repo.url}/edit/main/${schedule.source}`) ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        className="hard__pr hard__pr--plain"
        aria-label={`Takvimi değiştirmek için ${schedule.source} dosyasında PR aç, yeni sekmede açılır`}
      >
        <Icon name="git-pull-request" size={16} />
        Değiştirmek için PR
        <span className="mono hard__repo">{schedule.source}</span>
        <Icon name="external-link" size={14} className="ink-2" />
      </a>
    </div>
  );
}

export function DataBody({ settings }: { settings: Settings }) {
  const backup = settings.backup;
  const missing = backupMissing(settings);
  return (
    <div className="pol-block">
      <LoosenLegend />
      <h3 className="pol-sub key">Saklama süreleri</h3>
      {rows(settings, ['retention_screenshots', 'retention_messages', 'retention_audit']).map((policy) => (
        <PolicyRow
          key={policy.id}
          policy={policy}
          hint={`${policy.note ?? ''} · ${policy.loosens === 'up' ? 'uzatmak' : 'kısaltmak'}: basılı tut + gerekçe`}
        />
      ))}
      <h3 className="pol-sub key">Yedek</h3>
      <div className={missing ? 'backup backup--missing' : 'backup'}>
        <span className="backup__line">
          {missing ? <TriangleGlyph /> : <Icon name="check" size={12} stroke={2.5} />}
          <span>
            Son yedek:{' '}
            <span className="backup__value">
              {backup.lastBackupAt
                ? `${formatDayMonth(backup.lastBackupAt)} ${formatClock(backup.lastBackupAt)}`
                : 'yok'}
            </span>{' '}
            · geri yükleme testi:{' '}
            <span className="backup__value">
              {backup.lastRestoreTestAt
                ? `${formatDayMonth(backup.lastRestoreTestAt)} ${formatClock(backup.lastRestoreTestAt)}`
                : 'yok'}
            </span>
          </span>
        </span>
        <span className="backup__meta">
          {backup.target} · {backup.plan}
        </span>
        <Link to={`/ayarlar/baglanti/${backup.connection}`} className="backup__link">
          {missing ? 'Yedeği bağla' : 'Yedek bağlantısı'}
          <Icon name="chevron-right" size={16} />
        </Link>
      </div>
    </div>
  );
}

export function SecurityBody({ settings }: { settings: Settings }) {
  const security = settings.security;
  const untested = killSwitchUntested(settings);
  return (
    <div className="pol-block">
      <dl className="facts-list">
        <div className="facts-list__row">
          <dt>Oturum</dt>
          <dd>
            <span className="facts-list__strong">{security.session.method}</span>
            <span className="ink-2">
              {' '}
              · {security.session.identity ?? 'henüz yok'} · {security.session.note}
            </span>
          </dd>
        </div>
        <div className="facts-list__row">
          <dt>Cihazlar</dt>
          <dd>
            {security.devices.length === 0 ? (
              <span className="ink-2">Kayıtlı cihaz yok; Tailscale bağlanınca listelenir.</span>
            ) : (
              security.devices.map((device) => (
                <span key={device.name} className="facts-list__device">
                  {device.name}
                  {device.current ? ' · bu cihaz' : ''} · <span className="mono">{formatClock(device.lastSeenAt)}</span>
                </span>
              ))
            )}
          </dd>
        </div>
        <div className="facts-list__row">
          <dt>Denetim kaydı zinciri</dt>
          <dd>
            son doğrulama{' '}
            <span className="facts-list__strong">
              {security.auditChain.lastVerifiedAt
                ? `${formatDayMonth(security.auditChain.lastVerifiedAt)} ${formatClock(security.auditChain.lastVerifiedAt)}`
                : 'yok'}
            </span>
            <span className="ink-2"> · {security.auditChain.note}</span>
          </dd>
        </div>
        <div className="facts-list__row">
          <dt>Acil durdurma testi</dt>
          <dd>
            {untested ? (
              <span className="warn-chip">
                <TriangleGlyph />
                Yapılmadı
              </span>
            ) : (
              <span className="mono">
                {security.killSwitchTest.lastTestedAt
                  ? `${formatDayMonth(security.killSwitchTest.lastTestedAt)} ${formatClock(security.killSwitchTest.lastTestedAt)}`
                  : ''}
              </span>
            )}
            <span className="ink-2"> · {security.killSwitchTest.note}</span>
          </dd>
        </div>
      </dl>
      <McpAddresses />
    </div>
  );
}

/**
 * MCP adresleri (v0.1.215; Sercan, 20 Oct 2026: "tüm bilgilere mcp ile ulaşabilecek alt yapı", "şifresiz olsun"): the
 * address the Claude app or ChatGPT reads Twin through. Read only, personal details masked; shown once when made.
 */
export function McpAddresses() {
  const { store, state } = useMcpAddresses();
  const [label, setLabel] = useState('Claude uygulaması');
  const [copied, setCopied] = useState(false);
  const headingId = useId();
  const labelId = useId();
  useEffect(() => {
    void store.reload();
  }, [store]);
  const live = (state.addresses ?? []).filter((address) => address.revokedAt === null);
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <section className="mcp-sec" aria-labelledby={headingId}>
      <h3 id={headingId} className="key ink-2 mcp-sec__title">
        MCP adresleri (Claude ve ChatGPT)
      </h3>
      <p className="ink-2 mcp-sec__note">
        Bu adresi Claude uygulamasına ya da ChatGPT'ye bağlayıcı olarak eklersin; sohbetten Twin'in satış, tanıtım, SEO,
        kart ve maliyet bilgilerini okur. Yalnız okur, hiçbir şeyi değiştirmez. Müşteri adları, e-posta, telefon, adres
        ve indirme linkleri gizlenir. Adresin kendisi şifre yerine geçer: kimseyle paylaşma, sızarsa iptal et.
      </p>
      {state.created !== null ? (
        <div className="mcp-sec__created" role="status">
          <p>
            <strong>{state.created.label}</strong> için adres hazır. Şimdi kopyala; bir daha gösterilmeyecek.
          </p>
          <input
            className="field__input mono mcp-sec__url"
            readOnly
            value={state.created.url}
            aria-label="Yeni MCP adresi"
            onFocus={(event) => event.currentTarget.select()}
          />
          <div className="mcp-sec__acts">
            <button type="button" className="btn btn--primary" onClick={() => void copy(state.created?.url ?? '')}>
              {copied ? 'Kopyalandı' : 'Kopyala'}
            </button>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => {
                setCopied(false);
                store.dismissCreated();
              }}
            >
              Kopyaladım, kapat
            </button>
          </div>
        </div>
      ) : null}
      <form
        className="mcp-sec__form"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = label.trim();
          if (trimmed !== '') void store.create(trimmed);
        }}
      >
        <label htmlFor={labelId} className="key ink-2">
          Nerede kullanacaksın?
        </label>
        <div className="mcp-sec__acts">
          <input
            id={labelId}
            className="field__input"
            maxLength={60}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            disabled={state.busy}
          />
          <button type="submit" className="btn btn--primary" disabled={state.busy || label.trim() === ''}>
            Adres oluştur
          </button>
        </div>
      </form>
      {state.error !== null ? (
        <p className="mcp-sec__error" role="alert">
          {state.error}
        </p>
      ) : null}
      {state.addresses === null ? null : live.length === 0 ? (
        <p className="ink-2">Açık adres yok.</p>
      ) : (
        <ul className="mcp-sec__list">
          {live.map((address) => (
            <li key={address.id} className="mcp-sec__row">
              <span>
                <strong>{address.label}</strong>
                <span className="ink-2">
                  {' '}
                  · açıldı {formatDayMonth(Date.parse(address.createdAt))} · son kullanım{' '}
                  {address.lastUsedAt === null
                    ? 'henüz yok'
                    : `${formatDayMonth(Date.parse(address.lastUsedAt))} ${formatClock(Date.parse(address.lastUsedAt))}`}
                </span>
              </span>
              <button
                type="button"
                className="text-btn"
                disabled={state.busy}
                aria-label={`${address.label} adresini iptal et`}
                onClick={() => void store.revoke(address.id)}
              >
                İptal et
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AboutBody({ settings }: { settings: Settings }) {
  const about = settings.about;
  return (
    <div className="pol-block">
      <dl className="facts-list">
        <div className="facts-list__row">
          <dt>Sürüm</dt>
          <dd className="mono">{about.version}</dd>
        </div>
        <div className="facts-list__row">
          <dt>Commit</dt>
          <dd>
            <a
              href={safeHttpsUrl(`${about.repoUrl}/commit/${about.commit}`) ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="mono"
            >
              {about.commit}
            </a>
          </dd>
        </div>
        <div className="facts-list__row">
          <dt>CI</dt>
          <dd>
            {about.ci === 'passed' ? (
              <span className="ok-mark">
                <Icon name="check" size={12} stroke={2.5} />
                geçti
              </span>
            ) : about.ci === 'failed' ? (
              <span className="danger">kırmızı</span>
            ) : (
              'çalışıyor'
            )}{' '}
            · <span className="mono">{formatClock(about.ciAt)}</span> ·{' '}
            <a href={safeHttpsUrl(`${about.repoUrl}/actions`) ?? undefined} target="_blank" rel="noopener noreferrer">
              Actions
            </a>
          </dd>
        </div>
        <div className="facts-list__row">
          <dt>Repo</dt>
          <dd>
            <a
              href={safeHttpsUrl(about.repoUrl) ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              className="mono"
            >
              {about.repo}
            </a>
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function ChangeLogBody({ settings }: { settings: Settings }) {
  return (
    <div className="pol-block">
      <p className="pol-note">
        Politika ve bağlantı değişiklikleri: kim, ne zaman, ne değişti, gerekçe. Kayıt ekleme amaçlıdır, silinmez.
      </p>
      <AuditList entries={settings.audit} empty="Henüz değişiklik yok." />
    </div>
  );
}

/** Kurulum sırası: every counted connection in order; done ones last with a check. */
export function SetupBody({ settings }: { settings: Settings }) {
  const summary = setupSummary(settings);
  const done = settings.connections.filter(isLive);
  const disabledCount = summary.counts.disabled;
  return (
    <div className="pol-block">
      <ol className="setup-steps" aria-label="Kurulum sırası">
        {summary.steps.map((connection, index) => {
          const isNext = summary.next?.id === connection.id;
          return (
            <li key={connection.id} className={isNext ? 'setup-step setup-step--next' : 'setup-step'}>
              <span className={isNext ? 'setup-step__num setup-step__num--next mono' : 'setup-step__num mono'}>
                {index + 1}
              </span>
              <ServiceGlyph connection={connection} variant="bare" />
              <span className="setup-step__text">
                <Link to={`/ayarlar/baglanti/${connection.id}`} className="setup-step__title">
                  {connection.setup?.title ?? connection.name}
                </Link>
                <span className="setup-step__hint">
                  {connection.setup ? (
                    <Rich text={connection.setup.hint} />
                  ) : (
                    <Rich text={connectionPurpose(settings, connection)} />
                  )}
                </span>
              </span>
              <ConnectionPill status={connection.status} />
              <RowAction connection={connection} primary={isNext} small />
            </li>
          );
        })}
      </ol>
      {done.length > 0 ? (
        <>
          <h3 className="pol-sub key">Hazır</h3>
          <ul className="setup-steps" aria-label="Hazır bağlantılar">
            {done.map((connection) => (
              <li key={connection.id} className="setup-step setup-step--done">
                <span className="setup-step__num setup-step__num--done">
                  <Icon name="check" size={12} stroke={2.5} />
                </span>
                <ServiceGlyph connection={connection} variant="bare" />
                <span className="setup-step__text">
                  <Link to={`/ayarlar/baglanti/${connection.id}`} className="setup-step__title">
                    {connection.name}
                  </Link>
                  <span className="setup-step__hint">{connection.statusNote}</span>
                </span>
                <ConnectionPill status={connection.status} />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="pol-note">{disabledCount} kapalı bağlantı sayılmaz; politika açınca kuruluma eklenir.</p>
    </div>
  );
}
